<script setup lang="ts">
import { ref, watch } from "vue";
import Icon from "./Icon.vue";
import { api, emptyBilling, pln, when, type BillingProfile, type Invoice, type Pricing, type SupportRequest } from "../api";
import { href } from "../router";
import { invoicePill, supportPill } from "../status";
import BillingFields from "./BillingFields.vue";

// Rozliczenia klienta: dane do faktury, faktury wystawione przez admina i
// zgłoszenia do devopsa. Dane trzyma App (wspólne z pulpitem); tu widok i
// edycja danych nabywcy.

const props = defineProps<{ invoices: Invoice[]; requests: SupportRequest[]; pricing: Pricing | null; billing: BillingProfile | null }>();
const emit = defineEmits<{ reload: []; billingSaved: [BillingProfile] }>();

const editing = ref(false);
const draft = ref<BillingProfile>(emptyBilling());
const saving = ref(false);
const saveErr = ref("");
const saveOk = ref("");

function startEdit() {
  draft.value = props.billing ? { ...props.billing, nip: props.billing.nip ?? "", invoiceEmail: props.billing.invoiceEmail ?? "" } : emptyBilling();
  saveErr.value = "";
  saveOk.value = "";
  editing.value = true;
}
// Brak danych → formularz od razu otwarty; klient nie musi szukać przycisku.
watch(() => props.billing, (b) => { if (!b && !editing.value) startEdit(); }, { immediate: true });

async function save() {
  saving.value = true;
  saveErr.value = "";
  try {
    const d = await api.put<{ profile: BillingProfile }>("/api/billing-profile", draft.value);
    emit("billingSaved", d.profile);
    editing.value = false;
    saveOk.value = "Dane zapisane.";
  } catch (e) {
    saveErr.value = (e as Error).message;
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="page">
    <div class="head">
      <h1>Rozliczenia</h1>
      <button type="button" class="link" @click="emit('reload')"><Icon name="refresh" /> odśwież</button>
    </div>

    <div class="card" :class="{ warn: !billing }">
      <h2>Dane do faktury <span class="spacer" /><button v-if="billing && !editing" type="button" class="link" @click="startEdit">edytuj</button></h2>
      <div class="body">
        <template v-if="billing && !editing">
          <div class="buyer">
            <b>{{ billing.name }}</b>
            <span v-if="billing.nip">NIP {{ billing.nip }}</span>
            <span>{{ billing.address }}, {{ billing.postalCode }} {{ billing.city }}<template v-if="billing.country !== 'PL'">, {{ billing.country }}</template></span>
            <span v-if="billing.invoiceEmail" class="muted">faktury na: {{ billing.invoiceEmail }}</span>
          </div>
          <p v-if="saveOk" class="hint okc">{{ saveOk }}</p>
        </template>
        <form v-else @submit.prevent="save">
          <p v-if="!billing" class="notice">Uzupełnij dane nabywcy — bez nich nie uruchomisz płatnej naprawy. Sprawdzenie strony działa bez nich.</p>
          <BillingFields v-model="draft" id-prefix="bl" />
          <p v-if="saveErr" class="err">{{ saveErr }}</p>
          <div class="row">
            <button type="submit" :disabled="saving">{{ saving ? "Zapisuję…" : "Zapisz dane" }}</button>
            <button v-if="billing" type="button" class="sec" :disabled="saving" @click="editing = false">Anuluj</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="!invoices.length && !requests.length" class="card">
      <div class="body empty">
        <div class="big"><Icon name="receipt" :size="40" /></div>
        <p class="lead">Nie masz jeszcze żadnych rozliczeń</p>
        <p class="sub">
          Fakturę za naprawę wystawiamy po jej zakończeniu: {{ pricing?.description ?? "koszt analizy AI zaokrąglony w górę do pełnych 200 zł" }}.
          Pomoc devopsa to {{ pricing?.devopsHourlyPln ?? 200 }} zł za godzinę.
        </p>
      </div>
    </div>

    <div v-if="invoices.length" class="card">
      <h2>Faktury</h2>
      <div class="body scroll">
        <table>
          <thead><tr><th>Numer</th><th>Za</th><th>Kwota</th><th>Status</th><th>Data</th></tr></thead>
          <tbody>
            <tr v-for="i in invoices" :key="i.id">
              <td class="mono">{{ i.number }}</td>
              <td>
                {{ i.kind === "repair" ? "naprawę" : "pomoc devopsa" }}
                <a v-if="i.runId" class="muted" :href="href('naprawy', i.runId)"> — {{ i.runDomain }}</a>
                <span v-else-if="i.runDomain" class="muted"> — {{ i.runDomain }}</span>
                <div v-if="i.note" class="muted small">{{ i.note }}</div>
              </td>
              <td><b>{{ pln(i.amountPln) }}</b></td>
              <td><span class="pill" :class="invoicePill[i.status]?.cls">{{ invoicePill[i.status]?.text }}</span></td>
              <td class="muted">{{ when(i.issuedAt) }}</td>
            </tr>
          </tbody>
        </table>
        <p class="hint" style="margin-top:8px">Dokument faktury otrzymujesz e-mailem. Tu widzisz jej status.</p>
      </div>
    </div>

    <div v-if="requests.length" class="card">
      <h2>Zgłoszenia do devopsa</h2>
      <div class="body">
        <div v-for="r in requests" :key="r.id" class="item">
          <div class="row" style="justify-content:space-between">
            <span>
              <a v-if="r.runId" :href="href('naprawy', r.runId)"><b>{{ r.runDomain ?? "—" }}</b></a>
              <b v-else>{{ r.runDomain ?? "—" }}</b>
              <span class="muted"> • {{ when(r.createdAt) }}</span>
            </span>
            <span class="pill" :class="supportPill[r.status]?.cls">{{ supportPill[r.status]?.text }}</span>
          </div>
          <p v-if="r.message" class="msg">„{{ r.message }}”</p>
          <p v-if="r.adminNote" class="small"><b>Odpowiedź:</b> {{ r.adminNote }}</p>
          <p class="muted small">
            Przepracowano: {{ r.hours }} h
            <template v-if="r.invoice"> • faktura {{ r.invoice.number }} ({{ pln(r.invoice.amountPln) }})</template>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: var(--s4); }
.head h1 { font-size: 22px; font-weight: 750; letter-spacing: -.02em; }
.scroll { overflow-x: auto; }
.empty .big { font-size: 34px; opacity: .6; margin-bottom: var(--s2); }
.empty .lead { color: var(--fg); font-weight: 600; margin: 0 0 var(--s2); }
.empty .sub { max-width: 480px; margin: 0 auto; font-size: 12px; line-height: 1.6; }
.item { border: 1px solid var(--bd); border-radius: var(--r2); padding: var(--s3); background: var(--panel2); margin-bottom: var(--s2); font-size: 12px; }
.item a { color: inherit; text-decoration: none; }
.card.warn { border-color: var(--warn); }
.notice { font-size: 13px; background: var(--warn-dim); border-radius: var(--r1); padding: var(--s2) var(--s3); margin: 0 0 var(--s2); }
.buyer { display: flex; flex-direction: column; gap: 2px; font-size: 13px; }
.okc { color: var(--ok); }
.item a:hover { color: var(--acc); }
.msg { margin: var(--s2) 0; color: var(--fg); font-size: 13px; }
.small { font-size: 11px; margin: 2px 0; }
</style>
